import { z } from "zod";
import { slugString } from "./slug.js";
import { WEIGHT_KG_MAX, WEIGHT_KG_MIN } from "../units.js";

const ExerciseSlug = slugString(64);

// Where the MAX value came from. The distinction matters for trust:
//   manual         — user typed what they think they can hit today
//   tested         — confirmed in a workout as a TRUE single-rep PR
//                    (athlete actually attempted and made a single)
//   derived        — accepted from the post-save pill, which projects
//                    a conservative training-cap from a multi-rep set
//                    via Epley × 0.95 + 0.5 kg round. NOT a tested 1RM
//                    (added 1.67.3 after Antenore flagged the
//                    bench-press 5×77.5 → 86 kg suggestion: the value
//                    was being written as `tested` even though he
//                    never attempted a single).
//   calculated_seed — server seeded from e1RM after first import; honest
//                    bootstrap, but the user should re-confirm soon
//   imported       — pulled from another tracker (StrengthLog, etc.)
export const MaxSource = z.enum([
  "manual",
  "tested",
  "derived",
  "calculated_seed",
  "imported",
]);
export type MaxSource = z.infer<typeof MaxSource>;

// May this MAX be used to PROPOSE a load the athlete will actually lift?
//
// ⚠️ Deliberately NOT the same partition as `maxSuggest.ts`'s AUTO_SEED
// ({derived, calculated_seed, imported}), and the two must not be merged:
// that one answers "may the system OVERWRITE this value" — where `derived`
// belongs with the seeds because it is auto-origin and safe to refresh. This
// one answers "may the system hand this number to a human as a starting
// weight", and there `derived` sits with the trusted half: it was projected
// from a multi-rep set the athlete really performed, while a
// `calculated_seed` was bootstrapped at import time from data that may not
// be theirs at all.
//
// ⭐ Why the gate exists at all (EQ-1-SWAP-PCTMAX, 2026-08-20): converting a
// weight from one exercise to another THROUGH two MAXes multiplies both
// errors. Counted at the source on Antenore's own account that day: 65 of
// 115 MAXes were `calculated_seed`, and his 2026-05-28 audit-heal shows how
// far off they run — hip-abduction 85.5 → 56.5 kg (the value was 51% HIGH).
// Proposing a first set on an unfamiliar exercise from a number like that is
// the one failure mode worth refusing outright. `resolveDefaultWeight`
// already set the precedent: a percent with no known MAX leaves the cell
// blank rather than inventing a target.
const MAX_TRUSTED_FOR_PROPOSAL: ReadonlySet<MaxSource> = new Set<MaxSource>([
  "manual",
  "tested",
  "derived",
]);

export const isMaxTrustedForProposal = (
  source: MaxSource | undefined,
): boolean => source !== undefined && MAX_TRUSTED_FOR_PROPOSAL.has(source);

// Canonical storage in kg with gram precision, matching the rest of the
// app. Cap at 2000 kg, same as WorkoutSet.weight, so an order-of-magnitude
// typo (10000 instead of 100) is rejected at validation rather than
// landing in the cohort histogram.
export const Max = z.object({
  exerciseId: ExerciseSlug,
  valueKg: z.number().min(WEIGHT_KG_MIN).max(WEIGHT_KG_MAX),
  setAt: z.iso.datetime(),
  source: MaxSource,
  notes: z.string().max(200).optional(),
});
export type Max = z.infer<typeof Max>;

// Client → server payload. The server stamps setAt to "now" when the
// client omits it, but explicit setAt is allowed so a user can back-log
// a max they tested earlier without us forging a clock.
export const NewMax = Max.omit({ exerciseId: true, setAt: true }).extend({
  setAt: z.iso.datetime().optional(),
});
export type NewMax = z.infer<typeof NewMax>;
